import { clsx } from 'clsx';

import durationSvg from '@/assets/icons/duration.svg?raw';
import emailSvg from '@/assets/icons/email.svg?raw';
import goalSvg from '@/assets/icons/goal.svg?raw';
import homeSvg from '@/assets/icons/home-page_nav.svg?raw';
import lessonDefaultSvg from '@/assets/icons/lesson_default_generic.svg?raw';
import listeningSvg from '@/assets/icons/listening.svg?raw';
import lockedSvg from '@/assets/icons/locked.svg?raw';
import penSvg from '@/assets/icons/pen.svg?raw';
import profileSvg from '@/assets/icons/profile-nav.svg?raw';
import readingSvg from '@/assets/icons/reading.svg?raw';
import speakingSvg from '@/assets/icons/speaking.svg?raw';
import statsSvg from '@/assets/icons/stats.svg?raw';
import streakSvg from '@/assets/icons/streak.svg?raw';
import upgradeSvg from '@/assets/icons/upgrade_nav.svg?raw';
import usersSvg from '@/assets/icons/users.svg?raw';
import graduationSvg from '@/assets/icons/graduation-cap.svg?raw';
import paymentsSvg from '@/assets/icons/payment-card.svg?raw';
import clipboardSvg from '@/assets/icons/clipboard.svg?raw';
import checkCircleSvg from '@/assets/icons/check-circle.svg?raw';
import desktopSvg from '@/assets/icons/desktop.svg?raw';
import muteSvg from '@/assets/icons/mute.svg?raw';
import soundOnSvg from '@/assets/icons/sound-on.svg?raw';
import replaySvg from '@/assets/icons/replay.svg?raw';
import warningSvg from '@/assets/icons/warning.svg?raw';
import cameraSvg from '@/assets/icons/camera.svg?raw';
import trophySvg from '@/assets/icons/trophy.svg?raw';
import signalSvg from '@/assets/icons/signal.svg?raw';
import microphoneSvg from '@/assets/icons/microphone.svg?raw';
import calendarSvg from '@/assets/icons/calendar.svg?raw';
import starSvg from '@/assets/icons/star-milestone.svg?raw';
import saveSvg from '@/assets/icons/save.svg?raw';
import lightbulbSvg from '@/assets/icons/lightbulb.svg?raw';
import refreshSvg from '@/assets/icons/refresh.svg?raw';
import sadFaceSvg from '@/assets/icons/sad-face.svg?raw';
import sunSvg from '@/assets/icons/sun.svg?raw';
import moonSvg from '@/assets/icons/moon.svg?raw';
// Block S16 — mood rail faces (Phase4Speaking). Prefixed 'mood-' rather than
// bare 'sad'/'happy' etc. — 'sad-face' above is a pre-existing, unrelated icon.
import moodLaughingSvg from '@/assets/icons/mood-laughing.svg?raw';
import moodHappySvg from '@/assets/icons/mood-happy.svg?raw';
import moodNeutralSvg from '@/assets/icons/mood-neutral.svg?raw';
import moodSadSvg from '@/assets/icons/mood-sad.svg?raw';
import moodAngrySvg from '@/assets/icons/mood-angry.svg?raw';

// Each source SVG is normalized to viewBox + fill="currentColor" with no
// per-path fill overrides, so a single wrapper text color recolors the
// whole glyph uniformly. trial-period.svg is a shaded multi-tone
// illustration (not a flat glyph) and is intentionally excluded pending
// a design call on whether to flatten it.
const ICONS = {
  home: homeSvg,
  profile: profileSvg,
  streak: streakSvg,
  duration: durationSvg,
  email: emailSvg,
  goal: goalSvg,
  listening: listeningSvg,
  locked: lockedSvg,
  pen: penSvg,
  reading: readingSvg,
  speaking: speakingSvg,
  stats: statsSvg,
  upgrade: upgradeSvg,
  'lesson-default': lessonDefaultSvg,
  users: usersSvg,
  graduation: graduationSvg,
  payments: paymentsSvg,
  clipboard: clipboardSvg,
  'check-circle': checkCircleSvg,
  desktop: desktopSvg,
  mute: muteSvg,
  'sound-on': soundOnSvg,
  replay: replaySvg,
  warning: warningSvg,
  camera: cameraSvg,
  trophy: trophySvg,
  signal: signalSvg,
  microphone: microphoneSvg,
  calendar: calendarSvg,
  star: starSvg,
  save: saveSvg,
  lightbulb: lightbulbSvg,
  refresh: refreshSvg,
  'sad-face': sadFaceSvg,
  sun: sunSvg,
  moon: moonSvg,
  'mood-laughing': moodLaughingSvg,
  'mood-happy': moodHappySvg,
  'mood-neutral': moodNeutralSvg,
  'mood-sad': moodSadSvg,
  'mood-angry': moodAngrySvg,
} as const;

export type IconName = keyof typeof ICONS;

export function isIconName(v: string): v is IconName {
  return v in ICONS;
}

const SIZE_PX = {
  sm: 16,
  md: 20,
  lg: 28,
} as const;

// Reference (Nate's Irtaki hero page, .c-ico): 48px box / 12px radius / paper-2
// fill / amber-d icon for standalone feature icons — bare amber-on-plain-bg
// reads unfinished. Scaled down proportionally for sm/md so the same prop
// works at smaller feature moments, not just the reference's one 48px size.
const TILE_PX = {
  sm: 34,
  md: 40,
  lg: 48,
} as const;
const TILE_RADIUS = {
  sm: 'rounded-lg',
  md: 'rounded-[10px]',
  lg: 'rounded-xl',
} as const;

interface IconProps {
  name: IconName;
  size?: keyof typeof SIZE_PX;
  /** Wraps the glyph in a rounded tile, matching the reference's feature-card
   *  icon treatment. Omit for small/inline uses (nav, table cells, inline
   *  text) — those stay bare. `true` = paper-2 tile + amber-strong icon
   *  (standard). `'featured'` = amber tile + ink icon (dark/featured cards). */
  tile?: boolean | 'featured';
  className?: string;
}

// Renders in the wrapper's CSS `color` (default: amber-strong — a deeper,
// more restrained amber than the brighter primary `amber`, for a
// premium rather than bright/energetic read). Override color via a
// text-* className; override the box via width/height utilities.
export default function Icon({ name, size = 'md', tile = false, className }: IconProps) {
  const px = SIZE_PX[size];
  const glyph = (
    <span
      aria-hidden="true"
      className={clsx(
        'inline-block shrink-0 align-middle [&>svg]:w-full [&>svg]:h-full',
        tile ? (tile === 'featured' ? 'text-ink' : 'text-amber-strong') : 'text-amber-strong',
        !tile && className,
      )}
      style={{ width: px, height: px }}
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );

  if (!tile) return glyph;

  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center shrink-0',
        TILE_RADIUS[size],
        tile === 'featured' ? 'bg-amber' : 'bg-paper-2',
        className,
      )}
      style={{ width: TILE_PX[size], height: TILE_PX[size] }}
    >
      {glyph}
    </span>
  );
}
